import {
	Dialog,
	Form,
	FormItem,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { useDataInputDialog } from "client/Hooks";
import { isNotNilOrEmpty, resolveFormattedTextString } from "client/Misc/utils";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import {
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { videoUrlValidation } from "../Builder/validation";
import styles from "./VideoInputDialog.module.less";

export type VideoInputDialogProps = {
	videoUrl: string;
	onChange(videoUrl: string): void;
	onClose(): void;
};

export const VideoInputDialog = (props: VideoInputDialogProps): ReactElement => {
	const {
		videoUrl,
		onChange,
		onClose
	} = props;

	const [formData, setFormData] = useState<FormData>({ videoUrl });
	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			const validationResult: InputValidationResult = await formHandler.validation
				.validate();

			if (validationResult.kind === InputValidationResultKind.Invalid) {
				return;
			}

			onChange(formData.videoUrl);
		},
		[
			formData,
			formHandler,
			onChange
		]
	);

	const isVideoUrlValid: boolean = formHandler.for("videoUrl")
		.validation
		.isValid();

	return (
		<Dialog trackingId="video-input-dialog">
			<Dialog.Header
				title={localize(
					isNotNilOrEmpty(videoUrl)
						? "_EditVideo"
						: "_AddVideo"
					)}
				onClose={onClose}
			/>
			<Dialog.Body>
				<Form onSubmit={submitHandler}>
					<FormItem label={localize("_YoutubeOrVimeoVideoURL")}>
						<TextInput
							{...formHandler.for("videoUrl").attachToTextArea()}
							placeholder={
								resolveFormattedTextString({
									template: localize("_Examples"),
									replacements: [
										{
											what: "{{0}}",
											filler: "https://www.youtube.com/watch?v=abcd1234q5w"
										}
									]
								})
							}
						/>
						<ValidationError
							{...formHandler.for("videoUrl").attachToValidationError()}
						/>
						{!isVideoUrlValid && formData.videoUrl !== "" && (
							<VideoUrlExample />
						)}
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						text={localize(
							isNotNilOrEmpty(videoUrl)
								? "_Save"
								: "_Add"
						)}
						kind="primary"
						onClick={submitHandler}
						trackingId="save-button"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
VideoInputDialog.displayName = "VideoInputDialog";

const VideoUrlExample = (): ReactElement => {
	return (
		<div className={styles.videoUrlExample}>
			<p className={styles.videoUrlExampleParagraph}>{localize("_ExamplesOfValidVideoURLs")}</p>
			<ul className={styles.videoUrlExampleList}>
				<li>https://www.youtube.com/watch?v=abcd1234q5w</li>
				<li>https://www.vimeo.com/123456789</li>
			</ul>
			<p>{localize("_EmbeddedContentOtherThanAYoutubeAndVimeoVideoCanBeAddedByUsingTheEmbedButtonInTheEditor")}</p>
		</div>
	);
};
VideoUrlExample.displayName = "VideoUrlExample";

type FormData = {
	videoUrl: string;
};

const validationConfiguration = createValidationConfiguration<FormData>();

validationConfiguration.forScalar("videoUrl")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		videoUrlValidation
	);

export type VideoInputDialogData = Omit<VideoInputDialogProps, "onChange" | "onClose">;

export function useVideoInputDialog(
): [
	element: ReactElement | null,
	open: (data: VideoInputDialogData) => Promise<TryResult<string>>
] {
	return useDataInputDialog<VideoInputDialogData, string>(
		(openData, onClose, onSave) => {
			return (
				<VideoInputDialog
					{...openData}
					onClose={onClose}
					onChange={onSave}
				/>
			);
		}
	);
}